import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Typography,
    Paper,
    TextField,
    CircularProgress,
    Alert,
    Button,
    Switch,
    FormControlLabel,
    Chip,
    Box,
    Stack,
    Divider,
    Snackbar
} from "@mui/material";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import styled from "styled-components";
import { functions } from "../services/firebase";
import { captureError } from "../lib/errorReporting";
import { useIsMobile } from "../hooks/useIsMobile";
import { THEME_MAP } from "../theme";

const PageContainer = styled.div`
    padding: 24px;
    max-width: 900px;
    margin: 0 auto;
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 8px;

    svg {
        color: ${THEME_MAP["color-primary-500"]};
    }
`;

const Subtle = styled(Typography)`
    color: ${THEME_MAP["color-basic-600"]};
`;

export function SystemConfig() {
    const getSystem = useMemo(() => functions.httpsCallable("getOwnerEmailSystem"), []);
    const updateSettings = useMemo(() => functions.httpsCallable("updateOwnerEmailSettings"), []);
    const triggerRun = useMemo(() => functions.httpsCallable("triggerOwnerLifecycleEmails"), []);
    const getPushSystem = useMemo(
        () => functions.httpsCallable("getUserNotificationSystem"),
        []
    );
    const updatePushSettings = useMemo(
        () => functions.httpsCallable("updateUserNotificationSettings"),
        []
    );
    const triggerPushRun = useMemo(
        () => functions.httpsCallable("triggerUserLifecycleNotifications"),
        []
    );
    const isMobile = useIsMobile();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [toast, setToast] = useState("");

    const [settings, setSettings] = useState(null);
    const [allowlistInput, setAllowlistInput] = useState("");
    const [savingSettings, setSavingSettings] = useState(false);
    const [running, setRunning] = useState(false);

    const [pushSettings, setPushSettings] = useState(null);
    const [pushAllowlistInput, setPushAllowlistInput] = useState("");
    const [savingPush, setSavingPush] = useState(false);
    const [runningPush, setRunningPush] = useState(false);

    const loadSettings = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const [emailRes, pushRes] = await Promise.all([getSystem(), getPushSystem()]);
            setSettings(emailRes?.data?.settings || null);
            setPushSettings(pushRes?.data?.settings || null);
        } catch (err) {
            captureError(err, { view: "SystemConfig", action: "loadSettings" });
            setError(err?.message || "Failed to load system config.");
        } finally {
            setLoading(false);
        }
    }, [getSystem, getPushSystem]);

    useEffect(() => {
        loadSettings();
    }, [loadSettings]);

    const persistSettings = async (patch) => {
        setSavingSettings(true);
        setError("");
        try {
            const res = await updateSettings(patch);
            setSettings(res?.data?.settings || settings);
            setToast("Settings saved.");
        } catch (err) {
            captureError(err, { view: "SystemConfig", action: "updateSettings" });
            setError(err?.message || "Failed to save settings.");
        } finally {
            setSavingSettings(false);
        }
    };

    const addAllowlistEmail = () => {
        const email = allowlistInput.trim().toLowerCase();
        if (!email) return;
        const current = settings?.dev_allowlist || [];
        if (current.includes(email)) {
            setAllowlistInput("");
            return;
        }
        persistSettings({ dev_allowlist: [...current, email] });
        setAllowlistInput("");
    };

    const removeAllowlistEmail = (email) => {
        const current = settings?.dev_allowlist || [];
        persistSettings({ dev_allowlist: current.filter((e) => e !== email) });
    };

    const handleRun = async (dryRun) => {
        setRunning(true);
        setError("");
        try {
            await triggerRun({ dryRun });
            setToast(
                dryRun
                    ? "Dry run started — check the Email System activity log shortly."
                    : "Send run started — check the Email System activity log shortly."
            );
        } catch (err) {
            captureError(err, { view: "SystemConfig", action: "triggerRun" });
            setError(err?.message || "Failed to start the run.");
        } finally {
            setRunning(false);
        }
    };

    // ── Push notifications ───────────────────────────────────────────────────
    const persistPushSettings = async (patch) => {
        setSavingPush(true);
        setError("");
        try {
            const res = await updatePushSettings(patch);
            setPushSettings(res?.data?.settings || pushSettings);
            setToast("Settings saved.");
        } catch (err) {
            captureError(err, { view: "SystemConfig", action: "updatePushSettings" });
            setError(err?.message || "Failed to save settings.");
        } finally {
            setSavingPush(false);
        }
    };

    const addPushAllowlistUid = () => {
        const uid = pushAllowlistInput.trim();
        if (!uid) return;
        const current = pushSettings?.dev_allowlist || [];
        if (current.includes(uid)) {
            setPushAllowlistInput("");
            return;
        }
        persistPushSettings({ dev_allowlist: [...current, uid] });
        setPushAllowlistInput("");
    };

    const removePushAllowlistUid = (uid) => {
        const current = pushSettings?.dev_allowlist || [];
        persistPushSettings({ dev_allowlist: current.filter((u) => u !== uid) });
    };

    const handlePushRun = async (dryRun) => {
        setRunningPush(true);
        setError("");
        try {
            await triggerPushRun({ dryRun });
            setToast(
                dryRun
                    ? "Dry run started — check the Notification System activity log shortly."
                    : "Send run started — check the Notification System activity log shortly."
            );
        } catch (err) {
            captureError(err, { view: "SystemConfig", action: "triggerPushRun" });
            setError(err?.message || "Failed to start the run.");
        } finally {
            setRunningPush(false);
        }
    };

    if (loading) {
        return (
            <PageContainer>
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                    <CircularProgress />
                </Box>
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <Header>
                <TuneRoundedIcon fontSize="large" />
                <div>
                    <Typography variant="h5" sx={{ fontWeight: 800 }}>
                        System Config
                    </Typography>
                    <Subtle variant="body2">
                        Feature flags and system-level controls.
                    </Subtle>
                </div>
            </Header>

            {error ? (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
                    {error}
                </Alert>
            ) : null}

            {/* ── Owner Lifecycle Emails ───────────────────────────────────── */}
            <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    Owner Lifecycle Emails
                </Typography>
                <Subtle variant="body2" sx={{ mb: 2 }}>
                    Nothing is sent to real owners unless sending is enabled and dev mode is off (or the
                    recipient is on the allowlist).
                </Subtle>

                <Stack spacing={1}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={Boolean(settings?.sending_enabled)}
                                disabled={savingSettings}
                                onChange={(e) => persistSettings({ sending_enabled: e.target.checked })}
                            />
                        }
                        label="Sending enabled (master switch)"
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={Boolean(settings?.dev_mode)}
                                disabled={savingSettings}
                                onChange={(e) => persistSettings({ dev_mode: e.target.checked })}
                            />
                        }
                        label="Dev mode (only send to allowlisted addresses)"
                    />
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Dev allowlist
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                    {(settings?.dev_allowlist || []).length === 0 ? (
                        <Subtle variant="body2">No addresses yet.</Subtle>
                    ) : (
                        (settings?.dev_allowlist || []).map((email) => (
                            <Chip
                                key={email}
                                label={email}
                                onDelete={() => removeAllowlistEmail(email)}
                                disabled={savingSettings}
                            />
                        ))
                    )}
                </Stack>
                <Stack direction={isMobile ? "column" : "row"} spacing={1} alignItems="stretch">
                    <TextField
                        size="small"
                        fullWidth
                        placeholder="you@barhopperapp.com"
                        value={allowlistInput}
                        onChange={(e) => setAllowlistInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") addAllowlistEmail();
                        }}
                    />
                    <Button variant="outlined" onClick={addAllowlistEmail} disabled={savingSettings}>
                        Add
                    </Button>
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Stack direction={isMobile ? "column" : "row"} spacing={1}>
                    <Button variant="outlined" onClick={() => handleRun(true)} disabled={running}>
                        Dry run
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleRun(false)}
                        disabled={running}
                    >
                        Run now
                    </Button>
                </Stack>
            </Paper>

            {/* ── Push Notifications ───────────────────────────────────────── */}
            <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    Push Notifications
                </Typography>
                <Subtle variant="body2" sx={{ mb: 2 }}>
                    Nothing is pushed to real users unless sending is enabled and dev mode is off (or
                    the user is on the allowlist).
                </Subtle>

                <Stack spacing={1}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={Boolean(pushSettings?.sending_enabled)}
                                disabled={savingPush}
                                onChange={(e) =>
                                    persistPushSettings({ sending_enabled: e.target.checked })
                                }
                            />
                        }
                        label="Sending enabled (master switch)"
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={Boolean(pushSettings?.dev_mode)}
                                disabled={savingPush}
                                onChange={(e) => persistPushSettings({ dev_mode: e.target.checked })}
                            />
                        }
                        label="Dev mode (only send to allowlisted users)"
                    />
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Dev allowlist
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                    {(pushSettings?.dev_allowlist || []).length === 0 ? (
                        <Subtle variant="body2">No user ids yet.</Subtle>
                    ) : (
                        (pushSettings?.dev_allowlist || []).map((uid) => (
                            <Chip
                                key={uid}
                                label={uid}
                                onDelete={() => removePushAllowlistUid(uid)}
                                disabled={savingPush}
                            />
                        ))
                    )}
                </Stack>
                <Stack direction={isMobile ? "column" : "row"} spacing={1} alignItems="stretch">
                    <TextField
                        size="small"
                        fullWidth
                        placeholder="Firebase user UID"
                        value={pushAllowlistInput}
                        onChange={(e) => setPushAllowlistInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") addPushAllowlistUid();
                        }}
                    />
                    <Button variant="outlined" onClick={addPushAllowlistUid} disabled={savingPush}>
                        Add
                    </Button>
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Stack direction={isMobile ? "column" : "row"} spacing={1}>
                    <Button
                        variant="outlined"
                        onClick={() => handlePushRun(true)}
                        disabled={runningPush}
                    >
                        Dry run
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handlePushRun(false)}
                        disabled={runningPush}
                    >
                        Run now
                    </Button>
                </Stack>
            </Paper>

            <Snackbar
                open={Boolean(toast)}
                autoHideDuration={3000}
                onClose={() => setToast("")}
                message={toast}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            />
        </PageContainer>
    );
}
